import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Plus, Edit, Trash2, Play, Clock, Mail, FileText,
  Calendar, CheckCircle, XCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScheduledReportsManager = () => {
  const [scheduledReports, setScheduledReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [formData, setFormData] = useState({
    report_name: '',
    report_type: 'revenue',
    frequency: 'monthly',
    export_format: 'pdf',
    email_recipients: ''
  });

  useEffect(() => {
    fetchScheduledReports();
  }, []);

  const fetchScheduledReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/scheduled-reports`);
      setScheduledReports(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduled reports:', error);
      toast.error('Failed to load scheduled reports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingReport(null);
    setFormData({
      report_name: '',
      report_type: 'revenue',
      frequency: 'monthly',
      export_format: 'pdf',
      email_recipients: ''
    });
    setDialogOpen(true);
  };

  const handleEdit = (report) => {
    setEditingReport(report);
    setFormData({
      report_name: report.report_name,
      report_type: report.report_type,
      frequency: report.frequency,
      export_format: report.export_format,
      email_recipients: report.email_recipients.join(', ')
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        email_recipients: formData.email_recipients.split(',').map(e => e.trim()).filter(e => e)
      };

      if (editingReport) {
        await axios.put(`${API}/scheduled-reports/${editingReport.id}`, payload);
        toast.success('Scheduled report updated successfully');
      } else {
        await axios.post(`${API}/scheduled-reports`, payload);
        toast.success('Scheduled report created successfully');
      }

      setDialogOpen(false);
      fetchScheduledReports();
    } catch (error) {
      console.error('Failed to save scheduled report:', error);
      toast.error(error.response?.data?.detail || 'Failed to save scheduled report');
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Are you sure you want to delete "${report.report_name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/scheduled-reports/${report.id}`);
      toast.success('Scheduled report deleted successfully');
      fetchScheduledReports();
    } catch (error) {
      console.error('Failed to delete scheduled report:', error);
      toast.error('Failed to delete scheduled report');
    }
  };

  const handleRunNow = async (report) => {
    try {
      toast.info('Generating report...');
      await axios.post(`${API}/scheduled-reports/${report.id}/run`);
      toast.success('Report generated and will be sent to recipients');
      fetchScheduledReports();
    } catch (error) {
      console.error('Failed to run report:', error);
      toast.error('Failed to generate report');
    }
  };

  const handleToggleActive = async (report) => {
    try {
      await axios.put(`${API}/scheduled-reports/${report.id}`, {
        is_active: !report.is_active
      });
      toast.success(`Report ${!report.is_active ? 'activated' : 'deactivated'}`);
      fetchScheduledReports();
    } catch (error) {
      console.error('Failed to toggle report status:', error);
      toast.error('Failed to update report status');
    }
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly'
    };
    return labels[frequency] || frequency;
  };

  const getFormatBadge = (format) => {
    const styles = {
      pdf: 'bg-red-100 text-red-700',
      excel: 'bg-green-100 text-green-700',
      csv: 'bg-blue-100 text-blue-700'
    };
    return styles[format] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Scheduled Reports</h3>
          <p className="text-gray-600">Automate report generation and delivery</p>
        </div>
        <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="h-4 w-4 mr-2" />
          New Schedule
        </Button>
      </div>

      {/* Scheduled Reports List */}
      {scheduledReports.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Scheduled Reports</h3>
            <p className="text-gray-600 mb-4">Create your first scheduled report to automate delivery</p>
            <Button onClick={handleCreate} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Create Scheduled Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {scheduledReports.map((report) => (
            <Card key={report.id} className={!report.is_active ? 'opacity-60' : ''}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{report.report_name}</CardTitle>
                      {report.is_active ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="h-3 w-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="flex flex-wrap gap-4 text-sm">
                      <span className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        {report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {getFrequencyLabel(report.frequency)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="h-4 w-4" />
                        {report.email_recipients.length} recipient(s)
                      </span>
                      <Badge className={getFormatBadge(report.export_format)}>
                        {report.export_format.toUpperCase()}
                      </Badge>
                    </CardDescription>
                    {report.last_run && (
                      <p className="text-xs text-gray-500 mt-2">
                        Last run: {new Date(report.last_run).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRunNow(report)}
                      title="Run now"
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(report)}
                      title={report.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {report.is_active ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(report)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(report)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingReport ? 'Edit' : 'Create'} Scheduled Report</DialogTitle>
            <DialogDescription>
              Set up automatic report generation and email delivery
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Report Name *</Label>
              <Input
                value={formData.report_name}
                onChange={(e) => setFormData({ ...formData, report_name: e.target.value })}
                placeholder="e.g., Monthly Revenue Report"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Report Type *</Label>
                <select
                  value={formData.report_type}
                  onChange={(e) => setFormData({ ...formData, report_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                >
                  <option value="revenue">Revenue</option>
                  <option value="membership">Membership</option>
                  <option value="appointments">Appointments</option>
                  <option value="patients">Patients</option>
                </select>
              </div>

              <div>
                <Label>Frequency *</Label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Export Format *</Label>
              <select
                value={formData.export_format}
                onChange={(e) => setFormData({ ...formData, export_format: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              >
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
                <option value="csv">CSV</option>
              </select>
            </div>

            <div>
              <Label>Email Recipients * (comma-separated)</Label>
              <Input
                value={formData.email_recipients}
                onChange={(e) => setFormData({ ...formData, email_recipients: e.target.value })}
                placeholder="email1@example.com, email2@example.com"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter multiple email addresses separated by commas
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingReport ? 'Update' : 'Create'} Schedule
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledReportsManager;
